/**
 * Shared layout primitives extracted from the original PilotBody.tsx
 * monolith for Phase 1.5 of the React-engine port (see
 * `docs/eng/pilot-react-engine.md`). Pure mechanical extract — no
 * logic changes; every `id` / `data-*` / class hook is preserved
 * verbatim because the vanilla-JS engine binds against them.
 *
 * The leading underscore in the filename signals "module-internal" —
 * these are consumed only by the section components in this directory.
 */

import React, {useEffect, useRef, useState, type ReactNode} from 'react';
import {clsx} from 'clsx/lite';
import {Container} from '../../oatmeal/elements/container';
import {Eyebrow} from '../../oatmeal/elements/eyebrow';

/* ============================================================
   Scene primitive — every Story / Pitch / Notebook scene shares a
   centered Container with vertical rhythm and a top eyebrow.
   ============================================================ */
export function Scene({
  id,
  eyebrow,
  className,
  containerClassName,
  children,
}: {
  id?: string;
  eyebrow?: ReactNode;
  className?: string;
  /**
   * Extra utilities applied to the inner `<Container>`. Pitch uses this
   * to render each slide as a white card (with descendant text-colour
   * overrides) without the prior `body.pitch-mode .pitch > .story-scene
   * > .mx-auto` CSS gate in pilot-engine.css (F17 Slice C).
   */
  containerClassName?: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className={clsx(
        'flex min-h-screen w-full snap-start items-center justify-center py-16 sm:py-24',
        className,
      )}
    >
      <Container className={clsx('flex max-w-4xl flex-col gap-6', containerClassName)}>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        {children}
      </Container>
    </section>
  );
}

/* ============================================================
   Lane — a single race column (Today vs With Planapus).
   ============================================================ */
export function Lane({
  variant,
  title,
  clockId,
  svgId,
  caption,
}: {
  variant: 'human' | 'ai';
  title: string;
  clockId: string;
  svgId: string;
  caption?: string;
}) {
  return (
    <div
      className={clsx(
        'lane flex flex-col gap-3 rounded-2xl border border-taupe-200 bg-white p-5 dark:border-taupe-800 dark:bg-taupe-900',
        variant,
      )}
    >
      <div className="lane-header flex items-baseline justify-between border-b border-taupe-200 pb-3 dark:border-taupe-800">
        <div className="title text-sm/6 font-medium text-taupe-950 dark:text-white">
          {title}
        </div>
        <div className="flex flex-col items-end leading-none">
          <div
            className="clock font-display text-base/6 text-taupe-700 tabular-nums dark:text-taupe-400"
            id={clockId}
          >
            Day 0.0
          </div>
          <div className="text-[10px] uppercase tracking-widest text-taupe-500">
            this run
          </div>
        </div>
      </div>
      <svg
        id={svgId}
        viewBox="0 0 600 540"
        preserveAspectRatio="xMidYMid meet"
        className="w-full"
      />
      {caption ? (
        <p className="lane-caption text-xs/5 text-taupe-600 dark:text-taupe-500">
          {caption}
        </p>
      ) : null}
    </div>
  );
}

/* ============================================================
   Tooltip glyph — info dot used by metric prompts. The popover
   surfaces the explanation on hover, focus, or tap (mobile-friendly:
   tapping the dot toggles the popover open/closed).

   Per the May 2026 functional/content review, the prior `data-tt`
   attribute alone wasn't enough — no engine code read it, so the info
   box never appeared. This component now owns popover state directly
   so the explanation is always reachable.
   ============================================================ */
export function Tt({text}: {text: string}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent | TouchEvent) => {
      const root = ref.current;
      if (!root) return;
      const target = e.target as Node | null;
      if (target && root.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer, {passive: true});
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      ref={ref}
      className="relative ml-1 inline-flex align-baseline"
      data-tt={text}
    >
      <button
        type="button"
        aria-label={text}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex size-4 cursor-help items-center justify-center rounded-full border border-taupe-300 text-[10px] font-medium text-taupe-600 transition-colors hover:border-taupe-950 hover:text-taupe-950 dark:border-taupe-700 dark:text-taupe-400 dark:hover:border-taupe-100 dark:hover:text-taupe-100"
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-64 max-w-[80vw] -translate-x-1/2 rounded-lg border border-taupe-200 bg-white px-3 py-2 text-xs/5 font-normal text-taupe-700 shadow-lg dark:border-taupe-700 dark:bg-taupe-900 dark:text-taupe-300"
        >
          {text}
        </span>
      )}
    </span>
  );
}

/* ============================================================
   Skeleton placeholder — shown until the engine paints a value.
   F17 of the Oatmeal rebuild: styling fully inline-Tailwind (the
   prior `.pp-skel` rule + `@keyframes pp-skel-pulse` in
   pilot-engine.css are gone). Tailwind's built-in animate-pulse
   replaces the bespoke 1.6s opacity keyframe; min-w-[1.5em]
   preserves the original em-dash slot width.
   ============================================================ */
export function Skel() {
  return (
    <span
      className="inline-block min-w-[1.5em] animate-pulse text-taupe-400 align-middle [font-feature-settings:'tnum'] dark:text-taupe-600"
      aria-hidden
    >
      —
    </span>
  );
}

/* ============================================================
   Stat — the eyebrow + headline-number + label triplet used on
   the savings panels.
   ============================================================ */
export function Stat({
  numId,
  labelId,
  label,
  caption,
  initial = '—',
  scale = false,
}: {
  numId: string;
  labelId?: string;
  label: ReactNode;
  caption?: ReactNode;
  initial?: string;
  scale?: boolean;
}) {
  return (
    <div className={scale ? 'nb-scale-win flex flex-col gap-1' : 'nb-win flex flex-col gap-1'}>
      <div
        id={numId}
        className={clsx(
          'font-display tracking-tight text-taupe-950 dark:text-white',
          scale ? 'nb-scale-num text-4xl/none sm:text-5xl/none' : 'nb-win-num text-4xl/none sm:text-5xl/none',
        )}
      >
        {initial === '—' ? <Skel /> : initial}
      </div>
      <div
        id={labelId}
        className={clsx(
          'text-sm/6 text-taupe-700 dark:text-taupe-400',
          scale ? 'nb-scale-label' : 'nb-win-label',
        )}
      >
        {label}
      </div>
      {caption ? (
        <div className="nb-win-caption text-xs/5 text-taupe-600 dark:text-taupe-500">
          {caption}
        </div>
      ) : null}
    </div>
  );
}

/* ============================================================
   Modal — a taupe Oatmeal-styled modal frame. The engine toggles
   `.open` on `.modal-backdrop` (the `id` is the backdrop id).
   ============================================================ */
export function Modal({
  id,
  width = 'wide',
  children,
}: {
  id: string;
  width?: 'wide' | 'narrow';
  children: ReactNode;
}) {
  return (
    <div
      className="modal-backdrop fixed inset-0 z-40 hidden items-center justify-center bg-taupe-950/45 p-6 backdrop-blur-sm [&.open]:flex"
      id={id}
    >
      <div
        className={clsx(
          'modal flex max-h-[calc(100vh-3rem)] w-full flex-col overflow-hidden rounded-2xl border border-taupe-200 bg-white shadow-2xl dark:border-taupe-800 dark:bg-taupe-950',
          width === 'wide' ? 'modal-wide max-w-3xl' : 'modal-narrow max-w-md',
        )}
      >
        {children}
      </div>
    </div>
  );
}
